// src/features/tower/storeReadiness/components/ObservationDrawer.tsx

import React, { useState } from "react";
import type { EmployeeSummary, SopDefinition, ReadinessPermissions } from "../types";
import {
  createSopObservation,
  createBehaviourObservation,
  createConductIncident,
} from "../api";

type Mode = "sop" | "behaviour" | "conduct";

type Props = {
  branchId: string;
  sessionId: string;
  employees: EmployeeSummary[];
  sopDefinitions: SopDefinition[];
  permissions: ReadinessPermissions;
  onClose: () => void;
  onDone: () => void;
};

const CULTURAL_VALUES = ["OWN_IT", "BRING_THE_ENERGY", "SAY_IT_STRAIGHT", "GIVE_YOUR_BEST"];
const QUALITY_RESULTS = ["PASS", "COACHING_PASS", "FAIL", "NOT_OBSERVED"] as const;
const INCIDENT_TYPES = ["SAFETY", "PHONE_USE", "HYGIENE", "ATTITUDE", "OTHER"];

const fieldStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 7,
  border: "1px solid var(--ct-line)",
  fontFamily: "inherit",
  fontSize: 13,
  color: "var(--ct-ink)",
  outline: "none",
  marginBottom: 12,
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--ct-ink-2)",
  display: "block",
  marginBottom: 5,
};

const tabStyle = (on: boolean): React.CSSProperties => ({
  flex: 1,
  padding: "8px 0",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: on ? 700 : 500,
  border: "none",
  borderBottom: on ? "2px solid var(--ct-accent)" : "2px solid transparent",
  background: "transparent",
  color: on ? "var(--ct-accent)" : "var(--ct-ink-3)",
  cursor: "pointer",
});

const primaryBtnStyle: React.CSSProperties = {
  padding: "9px 16px",
  borderRadius: 8,
  border: "none",
  background: "var(--ct-accent)",
  color: "#fff",
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  width: "100%",
};

export function ObservationDrawer({
  branchId,
  sessionId,
  employees,
  sopDefinitions,
  permissions,
  onClose,
  onDone,
}: Props) {
  const [mode, setMode] = useState<Mode>("sop");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Shared
  const [employeeId, setEmployeeId] = useState("");
  const [observerEmployeeId, setObserverEmployeeId] = useState("");

  // SOP
  const [sopDefinitionId, setSopDefinitionId] = useState("");
  const [sopResult, setSopResult] = useState<typeof QUALITY_RESULTS[number]>("PASS");
  const [sopEvidenceUrl, setSopEvidenceUrl] = useState("");
  const [sopCoachingGiven, setSopCoachingGiven] = useState(false);
  const [sopNotes, setSopNotes] = useState("");

  // Behaviour
  const [culturalValue, setCulturalValue] = useState(CULTURAL_VALUES[0]);
  const [observationType, setObservationType] = useState<"POSITIVE" | "CORRECTIVE">("POSITIVE");
  const [rating, setRating] = useState(3);
  const [observableEvidence, setObservableEvidence] = useState("");
  const [secondManagerEmployeeId, setSecondManagerEmployeeId] = useState("");

  // Conduct
  const [incidentType, setIncidentType] = useState(INCIDENT_TYPES[0]);
  const [location, setLocation] = useState("");
  const [severity, setSeverity] = useState<"NON_CRITICAL" | "CRITICAL">("NON_CRITICAL");
  const [authorisedUse, setAuthorisedUse] = useState(false);
  const [description, setDescription] = useState("");
  const [actionTaken, setActionTaken] = useState("");

  const reset = () => {
    setSuccess(false);
    setError(null);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (mode === "sop") {
        await createSopObservation({
          employeeId,
          branchId,
          sessionId,
          sopDefinitionId,
          result: sopResult,
          observerEmployeeId,
          evidenceUrl: sopEvidenceUrl || undefined,
          coachingGiven: sopCoachingGiven,
          notes: sopNotes || undefined,
        });
      } else if (mode === "behaviour") {
        await createBehaviourObservation({
          employeeId,
          branchId,
          sessionId,
          culturalValue,
          observationType,
          rating,
          observableEvidence,
          observerEmployeeId,
          secondManagerEmployeeId,
        });
      } else {
        await createConductIncident({
          employeeId,
          branchId,
          sessionId,
          incidentType,
          location: location || undefined,
          severity,
          authorisedUse,
          observerEmployeeId,
          description,
          actionTaken: actionTaken || undefined,
        });
      }
      setSuccess(true);
      onDone();
    } catch (e: any) {
      setError(e?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmitSop = employeeId && observerEmployeeId && sopDefinitionId;
  const canSubmitBehaviour =
    permissions.canVerifyTasks &&
    employeeId && observerEmployeeId && secondManagerEmployeeId &&
    observableEvidence.trim().length > 0 && secondManagerEmployeeId !== observerEmployeeId;
  const canSubmitConduct =
    permissions.canViewConductData &&
    employeeId && observerEmployeeId && description.trim().length > 0;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.5)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          maxHeight: "88vh",
          overflowY: "auto",
          border: "1px solid var(--ct-line)",
          borderRadius: 12,
          background: "var(--ct-surface)",
          padding: 22,
          boxShadow: "0 20px 60px rgba(15,23,42,0.25)",
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 650, color: "var(--ct-ink)", marginBottom: 14 }}>
          Log an observation
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid var(--ct-line)", marginBottom: 16 }}>
          <button style={tabStyle(mode === "sop")} onClick={() => { setMode("sop"); reset(); }}>
            SOP check
          </button>
          <button style={tabStyle(mode === "behaviour")} onClick={() => { setMode("behaviour"); reset(); }}>
            Culture rating
          </button>
          <button style={tabStyle(mode === "conduct")} onClick={() => { setMode("conduct"); reset(); }}>
            Conduct incident
          </button>
        </div>

        {error && (
          <div style={{ fontSize: 12, color: "#991B1B", background: "#FEE2E2", borderRadius: 8, padding: "8px 12px", marginBottom: 14 }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ fontSize: 12, color: "#166534", background: "#DCFCE7", borderRadius: 8, padding: "8px 12px", marginBottom: 14 }}>
            Recorded successfully.
          </div>
        )}

        <label style={labelStyle}>Employee</label>
        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} style={fieldStyle}>
          <option value="">Select an employee…</option>
          {employees.map((emp) => (
            <option key={emp.employeeId} value={emp.employeeId}>{emp.employeeName}</option>
          ))}
        </select>

        <label style={labelStyle}>Observed by</label>
        <select value={observerEmployeeId} onChange={(e) => setObserverEmployeeId(e.target.value)} style={fieldStyle}>
          <option value="">Select an employee…</option>
          {employees.map((emp) => (
            <option key={emp.employeeId} value={emp.employeeId}>{emp.employeeName}</option>
          ))}
        </select>

        {mode === "sop" && (
          <>
            <label style={labelStyle}>SOP check</label>
            <select value={sopDefinitionId} onChange={(e) => setSopDefinitionId(e.target.value)} style={fieldStyle}>
              <option value="">Select a check…</option>
              {sopDefinitions.map((d) => (
                <option key={d.sopDefinitionId} value={d.sopDefinitionId}>
                  {d.sopName} ({d.marksAvailable} marks{d.criticality === "CRITICAL" ? " · Critical" : ""})
                </option>
              ))}
            </select>

            <label style={labelStyle}>Result</label>
            <select value={sopResult} onChange={(e) => setSopResult(e.target.value as typeof sopResult)} style={fieldStyle}>
              {QUALITY_RESULTS.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>

            <label style={labelStyle}>Evidence URL (optional)</label>
            <input value={sopEvidenceUrl} onChange={(e) => setSopEvidenceUrl(e.target.value)} style={fieldStyle} />

            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={sopCoachingGiven} onChange={(e) => setSopCoachingGiven(e.target.checked)} />
              Coaching given
            </label>

            <label style={labelStyle}>Notes (optional)</label>
            <textarea value={sopNotes} onChange={(e) => setSopNotes(e.target.value)} rows={2} style={{ ...fieldStyle, resize: "vertical" }} />

            <button
              disabled={!canSubmitSop || submitting}
              onClick={submit}
              style={{ ...primaryBtnStyle, opacity: !canSubmitSop || submitting ? 0.5 : 1 }}
            >
              {submitting ? "Saving…" : "Record SOP check"}
            </button>
          </>
        )}

        {mode === "behaviour" && (
          <>
            {!permissions.canVerifyTasks && (
              <div style={{ fontSize: 12, color: "var(--ct-ink-3)", marginBottom: 12 }}>
                Only a manager can record a culture rating.
              </div>
            )}

            <label style={labelStyle}>Cultural value</label>
            <select value={culturalValue} onChange={(e) => setCulturalValue(e.target.value)} style={fieldStyle}>
              {CULTURAL_VALUES.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
            </select>

            <label style={labelStyle}>Type</label>
            <select value={observationType} onChange={(e) => setObservationType(e.target.value as any)} style={fieldStyle}>
              <option value="POSITIVE">Positive</option>
              <option value="CORRECTIVE">Corrective</option>
            </select>

            <label style={labelStyle}>Rating (1–5)</label>
            <select value={rating} onChange={(e) => setRating(Number(e.target.value))} style={fieldStyle}>
              {[1, 2, 3, 4, 5].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>

            <label style={labelStyle}>Written evidence (required)</label>
            <textarea
              value={observableEvidence}
              onChange={(e) => setObservableEvidence(e.target.value)}
              rows={2}
              style={{ ...fieldStyle, resize: "vertical" }}
            />

            <label style={labelStyle}>Confirming second manager</label>
            <select value={secondManagerEmployeeId} onChange={(e) => setSecondManagerEmployeeId(e.target.value)} style={fieldStyle}>
              <option value="">Select an employee…</option>
              {employees.filter((e) => e.employeeId !== observerEmployeeId).map((emp) => (
                <option key={emp.employeeId} value={emp.employeeId}>{emp.employeeName}</option>
              ))}
            </select>

            <button
              disabled={!canSubmitBehaviour || submitting}
              onClick={submit}
              style={{ ...primaryBtnStyle, opacity: !canSubmitBehaviour || submitting ? 0.5 : 1 }}
            >
              {submitting ? "Saving…" : "Record culture rating"}
            </button>
          </>
        )}

        {mode === "conduct" && (
          <>
            {!permissions.canViewConductData && (
              <div style={{ fontSize: 12, color: "var(--ct-ink-3)", marginBottom: 12 }}>
                Only a manager can record a conduct incident.
              </div>
            )}

            <label style={labelStyle}>Incident type</label>
            <select value={incidentType} onChange={(e) => setIncidentType(e.target.value)} style={fieldStyle}>
              {INCIDENT_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>

            <label style={labelStyle}>Location (optional)</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} style={fieldStyle} />

            <label style={labelStyle}>Severity</label>
            <select value={severity} onChange={(e) => setSeverity(e.target.value as any)} style={fieldStyle}>
              <option value="NON_CRITICAL">Non-critical</option>
              <option value="CRITICAL">Critical</option>
            </select>

            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={authorisedUse} onChange={(e) => setAuthorisedUse(e.target.checked)} />
              Authorised use (e.g. approved phone use)
            </label>

            <label style={labelStyle}>Description (required)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} style={{ ...fieldStyle, resize: "vertical" }} />

            <label style={labelStyle}>Action taken (optional)</label>
            <textarea value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} rows={2} style={{ ...fieldStyle, resize: "vertical" }} />

            <button
              disabled={!canSubmitConduct || submitting}
              onClick={submit}
              style={{ ...primaryBtnStyle, opacity: !canSubmitConduct || submitting ? 0.5 : 1 }}
            >
              {submitting ? "Saving…" : "Record incident"}
            </button>
          </>
        )}

        <button
          onClick={onClose}
          style={{
            marginTop: 14,
            width: "100%",
            padding: "9px 16px",
            borderRadius: 8,
            border: "1px solid var(--ct-line)",
            background: "var(--ct-surface)",
            color: "var(--ct-ink-2)",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
